import xlsx from "xlsx/dist/xlsx.full.min.js";

/**
 * Mixin that replaces the blocking generateReport() with a non-blocking
 * version that dispatches to the global exportManager Vuex store.
 *
 * Survives component destruction (route navigation) by capturing the raw
 * Apollo client + snapshotting all query variables at call time, then
 * running fetches directly against the client.
 *
 * Requirements from the consuming component:
 *   - exportFileName()   → string (file name without .xlsx)
 *   - mapExportRow(item) → object
 *   - _getExportQuery()  → { query: gqlTag, root: "facilityXxx" }
 *   - _getExportVariables(page, limit) → variables object
 *   - Optional: exportSheetName() → string (defaults to exportFileName())
 *
 * OR (legacy): fetchForExporting(page, limit) if _getExportQuery is not defined
 *
 * For BuildingOverview (single-record, no pagination):
 *   - Set this.exportSingleRecord = true in data()
 *   - Provide buildExportRows() → array of mapped row objects
 */

function getApolloClient(vm) {
    // Vue-Apollo v4: vm.$apollo.provider.defaultClient
    // Vue-Apollo v3: vm.$apollo.provider.clients.defaultClient
    if (vm.$apollo && vm.$apollo.provider) {
        return vm.$apollo.provider.defaultClient || null;
    }
    return null;
}

var exportManagerMixin = {
    methods: {
        generateReport: function () {
            var store = this.$store;
            var apolloClient = getApolloClient(this);

            if (!apolloClient) {
                return;
            }

            var fileName = this.exportFileName() + ".xlsx";
            var sheetName =
                typeof this.exportSheetName === "function"
                    ? this.exportSheetName()
                    : this.exportFileName();

            // Single-record export: snapshot data immediately
            if (this.exportSingleRecord) {
                var singleRows = this.buildExportRows();
                store
                    .dispatch("exportManager/addJob", {
                        fileName: fileName,
                        totalSheets: 1,
                    })
                    .then(function (jobId) {
                        store.dispatch("exportManager/setMinimized", false);
                        doSingleRecordExport(store, jobId, fileName, sheetName, singleRows);
                    });
                return;
            }

            // Paginated export: snapshot everything needed for fetching
            var exportMeta = this._getExportQuery();
            var query = exportMeta.query;
            var root = exportMeta.root;

            // Snapshot current filter/entity/campus state into a variable builder
            var snapshotVarsFn = this._snapshotExportVariables();
            var mapRow = this.mapExportRow.bind(this);

            store
                .dispatch("exportManager/addJob", {
                    fileName: fileName,
                    totalSheets: 1,
                })
                .then(function (jobId) {
                    store.dispatch("exportManager/setMinimized", false);
                    store.dispatch("exportManager/updateJob", {
                        id: jobId,
                        progress: 5,
                        currentSheet: sheetName,
                    });

                    doPaginatedExport({
                        store: store,
                        apolloClient: apolloClient,
                        jobId: jobId,
                        fileName: fileName,
                        sheetName: sheetName,
                        query: query,
                        root: root,
                        buildVariables: snapshotVarsFn,
                        mapRow: mapRow,
                    });
                });
        },

        /**
         * Override in each module. Returns { query, root }.
         * Default: throw so we know if a module forgets.
         */
        _getExportQuery: function () {
            throw new Error("Module must implement _getExportQuery()");
        },

        /**
         * Returns a function(page, limit) → variables object.
         * Captures current component state at call time.
         * Override in the module if needed.
         */
        _snapshotExportVariables: function () {
            var entity = this.entity;
            var campus = this.campus;
            var search = this.search || null;

            var filtersCopy = {};
            if (this.filters) {
                var keys = Object.keys(this.filters);
                for (var i = 0; i < keys.length; i++) {
                    filtersCopy[keys[i]] = this.filters[keys[i]];
                }
            }

            return function (page, limit) {
                var vars = {
                    first: limit,
                    page: page,
                    entity: entity,
                    campus: campus,
                    search: search,
                };
                var fKeys = Object.keys(filtersCopy);
                for (var j = 0; j < fKeys.length; j++) {
                    vars[fKeys[j]] = filtersCopy[fKeys[j]] || null;
                }
                return vars;
            };
        },
    },
};

function doSingleRecordExport(store, jobId, fileName, sheetName, rows) {
    try {
        if (!rows || !rows.length) {
            store.dispatch("exportManager/failJob", {
                id: jobId,
                error: "No data to export",
            });
            return;
        }
        var worksheet = xlsx.utils.json_to_sheet(rows);
        var workbook = xlsx.utils.book_new();
        xlsx.utils.book_append_sheet(workbook, worksheet, sheetName);
        xlsx.writeFile(workbook, fileName);
        store.dispatch("exportManager/completeJob", {
            id: jobId,
            rowCount: rows.length,
        });
    } catch (error) {
        store.dispatch("exportManager/failJob", {
            id: jobId,
            error: (error && error.message) || "Export failed",
        });
    }
}

function doPaginatedExport(ctx) {
    var store = ctx.store;
    var apolloClient = ctx.apolloClient;
    var jobId = ctx.jobId;
    var fileName = ctx.fileName;
    var sheetName = ctx.sheetName;
    var query = ctx.query;
    var root = ctx.root;
    var buildVariables = ctx.buildVariables;
    var mapRow = ctx.mapRow;

    var allRows = [];
    var exportPage = 1;
    var exportLimit = 500;

    function fetchNext() {
        var variables = buildVariables(exportPage, exportLimit);

        apolloClient
            .query({
                query: query,
                variables: variables,
                fetchPolicy: "network-only",
            })
            .then(function (response) {
                var payload =
                    response && response.data && response.data[root]
                        ? response.data[root]
                        : null;

                if (!payload || payload.error) {
                    throw new Error(
                        (payload && payload.message) || "Failed to fetch data for export."
                    );
                }

                var rows = payload.data || [];
                if (rows.length) {
                    for (var i = 0; i < rows.length; i++) {
                        allRows.push(mapRow(rows[i]));
                    }
                }

                var paginator = payload.paginatorInfo || {};
                var hasMore = !!paginator.hasMorePages;
                var total = paginator.total || allRows.length;
                var percent =
                    total > 0
                        ? Math.min(95, Math.round((allRows.length / total) * 100))
                        : 50;

                store.dispatch("exportManager/updateJob", {
                    id: jobId,
                    progress: percent,
                    currentSheet: sheetName + " (" + allRows.length + " rows)",
                });

                exportPage += 1;

                if (hasMore && exportPage <= 100) {
                    fetchNext();
                } else {
                    finishExport();
                }
            })
            .catch(function (error) {
                store.dispatch("exportManager/failJob", {
                    id: jobId,
                    error: (error && error.message) || "Export failed",
                });
            });
    }

    function finishExport() {
        try {
            if (allRows.length === 0) {
                store.dispatch("exportManager/failJob", {
                    id: jobId,
                    error: "No records to export",
                });
                return;
            }
            var worksheet = xlsx.utils.json_to_sheet(allRows);
            var workbook = xlsx.utils.book_new();
            xlsx.utils.book_append_sheet(workbook, worksheet, sheetName);
            xlsx.writeFile(workbook, fileName);
            store.dispatch("exportManager/completeJob", {
                id: jobId,
                rowCount: allRows.length,
            });
        } catch (error) {
            store.dispatch("exportManager/failJob", {
                id: jobId,
                error: (error && error.message) || "Export failed",
            });
        }
    }

    fetchNext();
}

export default exportManagerMixin;
